import { useState } from "react";
import { Modal, Label, TextInput, Button } from "flowbite-react";

type Parcel = {
  date: string;
  amount: number;
};

type Errors = {
  value?: string;
  installments?: string;
  [key: string]: string | undefined;
};

interface ModalEditBilletDateProps {
  totalValue: number;
  onClose: () => void;
  onConfirm: (values) => void;
}

export const ModalEditBilletDate = ({
  totalValue,
  onClose,
}: ModalEditBilletDateProps) => {
  const [value, setValue] = useState<number>(totalValue || 0);
  const [installments, setInstallments] = useState<number>(1);
  const [parcels, setParcels] = useState<Parcel[]>([
    { date: "", amount: totalValue || 0 },
  ]);
  const [errors, setErrors] = useState<Errors>({});

  const redistributeParcels = (total: number, count: number) => {
    const amountPerParcel = total && count > 0 ? total / count : 0;
    const newParcels = Array.from({ length: count }, (_, i) => ({
      date: parcels[i]?.date || "",
      amount: Number(amountPerParcel.toFixed(2)),
    }));
    setParcels(newParcels);
  };

  const validate = (): boolean => {
    const newErrors: Errors = {};

    if (!value || value <= 0) {
      newErrors.value = "Valor deve ser maior que zero";
    }

    if (!installments || installments < 1) {
      newErrors.installments = "Mínimo 1 parcela";
    }

    parcels.forEach((p, i) => {
      if (!p.date) newErrors[`date_${i}`] = "Data obrigatória";
      if (!p.amount || p.amount <= 0)
        newErrors[`amount_${i}`] = "Valor deve ser maior que zero";
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (!validate()) return;
    onClose();
  };

  return (
    <Modal show={true} onClose={onClose}>
      <Modal.Header className="text-black">Informações do Boleto</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          <div>
            <Label htmlFor="value" value="Valor Total" className="text-black" />
            <TextInput
              id="value"
              type="number"
              step="0.01"
              value={value}
              onChange={(e) => {
                const newValue = parseFloat(e.target.value) || 0;
                setValue(newValue);
                redistributeParcels(newValue, installments);
              }}
              placeholder="Ex: 1200.00"
            />
            {errors.value && (
              <p className="text-red-500 text-sm mt-1">{errors.value}</p>
            )}
          </div>

          <div>
            <Label
              htmlFor="installments"
              value="Quantidade de Parcelas"
              className="text-black"
            />
            <TextInput
              id="installments"
              type="number"
              min="1"
              value={installments}
              onChange={(e) => {
                const count = Number(e.target.value) || 1;
                setInstallments(count);
                redistributeParcels(value, count);
              }}
              placeholder="Ex: 3"
            />
            {errors.installments && (
              <p className="text-red-500 text-sm mt-1">
                {errors.installments}
              </p>
            )}
          </div>

          <div className="space-y-3">
            {parcels.map((parcel, index) => (
              <div
                key={index}
                className="grid grid-cols-2 gap-4 border p-3 rounded-md"
              >
                <div>
                  <Label
                    htmlFor={`parcel-date-${index}`}
                    value={`Data da Parcela ${index + 1}`}
                  />
                  <TextInput
                    id={`parcel-date-${index}`}
                    type="date"
                    value={parcel.date}
                    onChange={(e) => {
                      const newParcels = [...parcels];
                      newParcels[index].date = e.target.value;
                      setParcels(newParcels);
                    }}
                  />
                  {errors[`date_${index}`] && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors[`date_${index}`]}
                    </p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor={`parcel-amount-${index}`}
                    value={`Valor da Parcela ${index + 1}`}
                  />
                  <TextInput
                    id={`parcel-amount-${index}`}
                    type="number"
                    step="0.01"
                    value={parcel.amount}
                    onChange={(e) => {
                      const newParcels = [...parcels];
                      newParcels[index].amount =
                        parseFloat(e.target.value) || 0;
                      setParcels(newParcels);
                    }}
                    placeholder="Ex: 400.00"
                  />
                  {errors[`amount_${index}`] && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors[`amount_${index}`]}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button color="gray" onClick={onClose} type="button">
              Cancelar
            </Button>
            <Button color="failure" onClick={handleConfirm} type="button">
              Confirmar
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

